import { type JSX } from 'react';

import { useHydrateEventsIntoStore } from 'queries/markets';
import { useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';
import { getSeriesImageSrc } from 'utils/images';

import SquareImage from './SquareImage';
import type { ImageFetchPriority, ImageLoading } from './types';

const NO_TICKERS: string[] = [];

interface SquareSeriesImageProps {
    seriesTicker?: string;
    eventTicker?: string;
    customImageUrl?: string;
    alt?: string;
    size?: number;
    className?: string;
    loading?: ImageLoading;
    fetchPriority?: ImageFetchPriority;
}

const SquareSeriesImage = ({
    seriesTicker,
    eventTicker,
    customImageUrl,
    alt = 'Series Image',
    size = 48,
    className,
    loading = 'lazy',
    fetchPriority = 'auto',
}: SquareSeriesImageProps): JSX.Element => {
    const event = useMarketEntitiesStore((state) => (eventTicker ? state.events[eventTicker] : undefined));
    // Only hydrate the single event when it isn't already in the store. Callers that render many rows
    // (e.g. navbar search) batch-hydrate the whole dropdown up front, so gating here restores the old
    // `event.loaded` guard and avoids one redundant `/events?tickers=<ticker>` request per row.
    const tickers = !customImageUrl && eventTicker && !event ? [eventTicker] : NO_TICKERS;
    useHydrateEventsIntoStore(tickers);

    // Prefer the explicit image, then the event's own image, then the series default. Falling back to
    // the series image (instead of blank) while the event hydrates keeps a stable image at call sites
    // that only pass series/event tickers (portfolio & event rows, social posts) — the Zustand store is
    // often empty there, so waiting on the redundant `/events` fetch previously left the image blank,
    // permanently so on a slow or failed request.
    const imageSrc = customImageUrl
        ? customImageUrl
        : event?.imageUrl
          ? event.imageUrl
          : seriesTicker
            ? getSeriesImageSrc(seriesTicker)
            : '';

    return (
        <SquareImage
            src={imageSrc}
            alt={alt}
            size={size}
            className={className}
            loading={loading}
            fetchPriority={fetchPriority}
        />
    );
};

export default SquareSeriesImage;
